import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostVideoCreditsResult,
  PostVideoCreditsOptions,
  GetVideoCreditsOptions,
  GetVideoCreditsResult,
  getVideoCredits,
  PostVideoCreditsFields,
  postVideoCredits,
  GetVideoCreditsFields,
} from '../fetchers/video/credits';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoCreditsOptions<Fields extends GetVideoCreditsFields> =
  GetVideoCreditsOptions<Fields>;

/**
 * Get all the credited users in a video
 * This method returns every credited user in a video.
 */

export function useGetVideoCredits<Fields extends GetVideoCreditsFields>(
  getOptions: UseGetVideoCreditsOptions<Fields> | (() => UseGetVideoCreditsOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoCreditsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/videos/${options.where.videoId}/credits${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getVideoCredits({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoCredits, {
    endpoint: '/videos/:videoId/credits',
    method: 'GET',
  });
}

/**
 * Get all the credited users in a video
 * This method returns every credited user in a video.
 */

export function useGetVideoCreditsLazy<Fields extends GetVideoCreditsFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetVideoCreditsResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetVideoCreditsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/credits${serializeQuery(options)}`,
          getVideoCredits({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoCreditsLazy, {
    endpoint: '/videos/:videoId/credits',
    method: 'GET',
  });
}

type UseGetVideoCreditsOptionsInfinite<Fields extends GetVideoCreditsFields> =
  GetVideoCreditsOptions<Fields>;

/**
 * Get all the credited users in a video
 * This method returns every credited user in a video.
 */

export function useGetVideoCreditsInfinite<Fields extends GetVideoCreditsFields>(
  getOptions:
    | UseGetVideoCreditsOptionsInfinite<Fields>
    | (() => UseGetVideoCreditsOptionsInfinite<Fields> | null),
  config?: SWRInfiniteConfiguration<GetVideoCreditsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let getKey = (pageIndex: number, previousPageData: GetVideoCreditsResult<Fields> | null) => {
    if (options === null) {
      return null;
    }
    if (previousPageData && !previousPageData.paging.next) {
      return null;
    }
    let { perPage = 25, page: _page, ...rest } = options.query ?? {};
    let select = options.select.join(',');
    let queryString = Object.entries(rest ?? {})
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return [
      `/videos/${options.where.videoId}/credits?page=${
        pageIndex + 1
      }&perPage=${perPage}&fields=${select}&${queryString}`,
      pageIndex,
    ];
  };
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWRInfinite(
    getKey,
    options !== null
      ? ([_uri, pageIndex]: [string, number]) => {
          return getVideoCredits({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            query: { ...options.query, page: pageIndex + 1 },
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoCreditsInfinite, {
    endpoint: '/videos/:videoId/credits',
    method: 'GET',
  });
}

type UsePostVideoCreditsOptions<Fields extends PostVideoCreditsFields> =
  PostVideoCreditsOptions<Fields>;

/**
 * Credit a user in a video
 * This method adds a user credit to the specified video.
 */

export function usePostVideoCredits<Fields extends PostVideoCreditsFields>() {
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PostVideoCreditsResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePostVideoCreditsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await postVideoCredits({
          ...options,
          baseUrl,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });
        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostVideoCredits, {
    endpoint: '/videos/:videoId/credits',
    method: 'POST',
  });
}
